import type { OrgHealthScore } from '../../types'

// The `key: keyof OrgHealthScore` annotation is what keeps the driver arrays below
// from widening their keys to plain strings.
type Driver = { key: keyof OrgHealthScore; label: string }

const CLASSIFICATION_STYLES = {
  Healthy: {
    badge: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
    text: 'text-green-600 dark:text-green-400'
  },
  Monitor: {
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    text: 'text-amber-600 dark:text-amber-400'
  },
  'At Risk': {
    badge: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
    text: 'text-red-600 dark:text-red-400'
  }
}

export const getClassificationStyle = (
  classification?: OrgHealthScore['health_classification']
) =>
  (classification && CLASSIFICATION_STYLES[classification]) || {
    badge: 'bg-accent text-muted-foreground',
    text: 'text-foreground'
  }

// A gate other than gate_passed forces At Risk regardless of score. 'zero usage' is
// the one value the upstream model writes with a space instead of an underscore.
const GATE_LABELS: Record<string, string> = {
  days_lt_14: 'Active fewer than 14 days in 90d',
  'zero usage': 'No usage in 90d',
  util_lt_5pct: 'Under 5% license utilization',
  users_lt_2: 'Fewer than 2 active users'
}

/** Why the classification is what it is — the score alone doesn't say. */
export const getHealthReason = (health?: OrgHealthScore): string | null => {
  if (!health?.health_classification) {
    return null
  }
  const gate = health.engagement_gate
  if (gate && gate !== 'gate_passed') {
    return GATE_LABELS[gate] ?? gate
  }
  if (health.relationship_gate) {
    return 'No CSM contact in 90d'
  }
  return health.health_classification === 'At Risk' ? 'Score below 40' : null
}

const STRATEGIC_DRIVERS: Driver[] = [
  { key: 'v_people', label: 'People' },
  { key: 'v_iterations', label: 'Iterations' },
  { key: 'v_project_progression', label: 'Project Progression' },
  { key: 'v_investment_profile', label: 'Investment Profile' },
  { key: 'v_forecast', label: 'Forecast' },
  { key: 'has_costcap_bonus', label: 'Cost cap bonus' }
]

const AI_DRIVERS: Driver[] = [
  { key: 'wb_events_90d', label: 'WorkerB' },
  { key: 'v_ai_insights', label: 'AI Insights' },
  { key: 'v_analytics', label: 'AI Analytics' },
  { key: 'mcp_requests_90d', label: 'MCP' },
  { key: 'has_gs_review_bonus', label: 'gitStream AI review bonus' }
]

// The five pillars sum exactly to total_score
export const PILLARS: {
  key: keyof OrgHealthScore
  label: string
  max: number
  hint: string
  drivers?: Driver[]
}[] = [
  {
    key: 'p1_consistency',
    label: 'Consistency',
    max: 30,
    hint: 'Active days in the last 90'
  },
  {
    key: 'p2_team',
    label: 'Team & utilization',
    max: 15,
    hint: 'Active users and license utilization'
  },
  {
    key: 'p3_strategic',
    label: 'Strategic adoption',
    max: 25,
    hint: '5 points per strategic surface used',
    drivers: STRATEGIC_DRIVERS
  },
  {
    key: 'p4_ai_ecosystem',
    label: 'AI ecosystem',
    max: 20,
    hint: '5 points each for WorkerB, AI Insights, AI Analytics and MCP',
    drivers: AI_DRIVERS
  },
  {
    key: 'p5_commercial',
    label: 'Commercial',
    max: 10,
    hint: 'Family versus parent commercial signal'
  }
]

// Targets the Radix Indicator inside LBProgress, which hardcodes bg-primary and
// exposes no styling API. Breaks silently if buzz ever wraps that child div.
export const getBarColor = (ratio: number) => {
  if (ratio >= 0.8) {
    return '[&>div]:bg-green-500'
  }
  return ratio >= 0.5 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
}
